import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import { recommendArtifacts, getPresetFromBrief, getPresetOptions, getThreatModelPreview } from '../services/artifactRecommendationService';
import '../styles/artifacts.css';

function Artifacts() {
  const navigate = useNavigate();
  const { sessionData, updateSessionData } = useSession();
  const [currentPreset, setCurrentPreset] = useState('Starter');
  const [showAll, setShowAll] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [presetInfo, setPresetInfo] = useState(null);
  const [selectedCount, setSelectedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [showThreatModelPreview, setShowThreatModelPreview] = useState(false);
  const [hasAutoDetected, setHasAutoDetected] = useState(false);

  useEffect(() => {
    if (!sessionData?.projectBrief) {
      navigate('/architecture-design');
      return;
    }

    // Auto-detect preset from brief only once on initial load
    if (!hasAutoDetected) {
      const suggestedPreset = getPresetFromBrief(sessionData.projectBrief);
      setCurrentPreset(suggestedPreset);
      setHasAutoDetected(true);
    }
  }, [sessionData, navigate, hasAutoDetected]);

  useEffect(() => {
    if (sessionData?.projectBrief) {
      const result = recommendArtifacts(sessionData.projectBrief, currentPreset, showAll);
      console.log('🔍 Artifacts Debug:', {
        preset: currentPreset,
        showAll,
        totalRecommendations: result.recommendations.length,
        selectedCount: result.selectedCount,
        coreCount: result.recommendations.filter(r => r.source === 'core').length,
        additionalCount: result.recommendations.filter(r => r.source !== 'core').length
      });
      setRecommendations(result.recommendations);
      setPresetInfo(result.preset);
      setSelectedCount(result.selectedCount);
      setTotalCount(result.totalCount);
    }
  }, [sessionData, currentPreset, showAll]);

  const handleSelectionChange = (artifactId, selected) => {
    setRecommendations(prev => {
      const updated = prev.map(rec => 
        rec.id === artifactId ? { ...rec, selected } : rec
      );
      setSelectedCount(updated.filter(r => r.selected).length);
      return updated;
    });
  };

  const handlePresetChange = (preset) => {
    setCurrentPreset(preset);
  };

  const handleShowAllToggle = () => {
    setShowAll(!showAll);
  };

  const handleGenerate = () => {
    const selectedArtifacts = recommendations.filter(rec => rec.selected).map(rec => rec.id);
    
    // Store selected artifacts in session using context method
    updateSessionData({
      selectedArtifacts,
      currentPreset
    });
    
    navigate('/generation');
  };

  const getSourceBadge = (source) => {
    const badges = {
      'core': { text: 'Core', class: 'source-core' },
      'related': { text: 'Related', class: 'source-related' },
      'additional': { text: 'Optional', class: 'source-additional' }
    };
    return badges[source] || { text: 'Other', class: 'source-other' };
  };

  const getDifficultyClass = (difficulty) => {
    return `difficulty-${difficulty.toLowerCase()}`;
  };

  const coreRecommendations = recommendations.filter(rec => rec.source === 'core');
  const additionalRecommendations = recommendations.filter(rec => rec.source !== 'core');
  const presetOptions = getPresetOptions();

  if (!sessionData?.projectBrief) {
    return <div>Loading...</div>;
  }

  return (
    <div className="artifacts-page">
      <div className="artifacts-header">
        <h1>Recommended Artifacts</h1>
        <p>Based on your project brief, here are the recommended documentation artifacts. 
           All acronyms are spelled out with clear explanations of what each artifact provides.</p>
      </div>

      <div className="project-summary">
        <h2>{sessionData.projectBrief.projectName}</h2>
        <p>{sessionData.projectBrief.description}</p>
      </div>

      <div className="preset-controls">
        <div className="preset-selector">
          <label>Pack Preset:</label>
          <select value={currentPreset} onChange={(e) => handlePresetChange(e.target.value)}>
            {presetOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        
        <div className="view-toggle">
          <label>
            <input
              type="checkbox"
              checked={showAll}
              onChange={handleShowAllToggle}
            />
            Show all artifacts ({totalCount})
          </label>
        </div>
      </div>

      <div className="preset-description">
        <p><strong>{presetInfo?.name}:</strong> {presetInfo?.description}</p>
      </div>

      <div className="selection-summary">
        <div className="selection-meter">
          <span className="meter-text">{selectedCount}/{presetInfo?.maxAutoSelect} core artifacts selected</span>
          <div className="meter-bar">
            <div 
              className="meter-fill" 
              style={{ width: `${Math.min(100, (selectedCount / (presetInfo?.maxAutoSelect || 1)) * 100)}%` }}
            ></div>
          </div>
        </div>
      </div>

      <div className="recommendations-section">
        <h3>Core Recommendations</h3>
        <div className="recommendations-grid">
          {coreRecommendations.map(rec => {
            const sourceBadge = getSourceBadge(rec.source);
            return (
              <div key={rec.id} className={`recommendation-card ${rec.selected ? 'selected' : ''}`}>
                <div className="card-header">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={rec.selected}
                      onChange={(e) => handleSelectionChange(rec.id, e.target.checked)}
                    />
                    <span className="artifact-name">{rec.name}</span>
                  </label>
                  <div className="badges">
                    <span className={`source-badge ${sourceBadge.class}`}>
                      {sourceBadge.text}
                    </span>
                    <span className={`difficulty-badge ${getDifficultyClass(rec.difficulty)}`}>
                      {rec.difficulty}
                    </span>
                  </div>
                </div>
                
                <div className="card-body">
                  <p className="reason">{rec.reason}</p>
                  
                  {rec.id === 'TM' && (
                    <div className="special-preview">
                      <button 
                        className="preview-button"
                        onClick={() => setShowThreatModelPreview(!showThreatModelPreview)}
                      >
                        {showThreatModelPreview ? 'Hide' : 'Show'} Template Preview
                      </button>
                      {showThreatModelPreview && (
                        <div className="threat-model-preview">
                          <h4>Threat Model Template Sections:</h4>
                          <ul>
                            {getThreatModelPreview().sections.map((section, idx) => (
                              <li key={idx}>{section}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {showAll && additionalRecommendations.length > 0 && (
          <>
            <div className="additional-section">
              <h3>Additional Artifacts ({additionalRecommendations.length})</h3>
              <p>Related and optional artifacts you can add to your documentation set.</p>
            </div>
            <div className="recommendations-grid">
              {additionalRecommendations.map(rec => {
                const sourceBadge = getSourceBadge(rec.source);
                return (
                  <div key={rec.id} className={`recommendation-card ${rec.selected ? 'selected' : ''}`}>
                    <div className="card-header">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={rec.selected}
                          onChange={(e) => handleSelectionChange(rec.id, e.target.checked)}
                        />
                        <span className="artifact-name">{rec.name}</span>
                      </label>
                      <div className="badges">
                        <span className={`source-badge ${sourceBadge.class}`}>
                          {sourceBadge.text}
                        </span>
                        <span className={`difficulty-badge ${getDifficultyClass(rec.difficulty)}`}>
                          {rec.difficulty}
                        </span>
                      </div>
                    </div>
                    
                    <div className="card-body">
                      <p className="reason">{rec.reason}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="actions-section">
        <button 
          className="btn-secondary"
          onClick={() => navigate('/review')}
        >
          ← Back to Project Brief
        </button>
        
        <button 
          className="btn-primary"
          onClick={handleGenerate}
          disabled={selectedCount === 0}
        >
          Generate Selected Artifacts ({selectedCount})
        </button>
      </div>
    </div>
  );
}

export default Artifacts;